/**
 * @file ShopGate.tsx
 *
 * Post-authentication bootstrap that gates the admin behind an eligible plan and a
 * resolved shop. Rendered as a child of {@link ../auth/AuthProvider}, so it runs
 * only once authenticated. On each attempt it:
 *   1. fetches the project (`GET /v1/projects`) and checks plan eligibility; an
 *      ineligible plan shows the not-eligible screen (the admin never renders),
 *      unless the `ALLOW_ALL_PLANS` config flag bypasses the check (it mirrors
 *      the backend's `provisioning.allowAllPlans`),
 *   2. otherwise gets the shop (`GET /v1/shops`); on a 404 it creates it
 *      (`POST /v1/shops`) and re-fetches — the client-driven get-or-create flow,
 *   3. provides the resolved shop via {@link ./shop-context} and renders the app.
 * Any failure shows the error screen with a retry that re-runs the whole bootstrap.
 *
 * The backend is authoritative on eligibility and creates idempotently, so the
 * double invocation React StrictMode does in development is harmless here.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ALLOW_ALL_PLANS } from '../config';
import { getProject } from '../api/project';
import { createShop, getShop, type Shop } from '../api/shop';
import { messages } from '../i18n/messages';
import { isShopEligible } from './eligibility';
import { NoticeScreen } from './NoticeScreen';
import { PlanNotEligibleScreen } from './PlanNotEligibleScreen';
import { ShopErrorScreen } from './ShopErrorScreen';
import { ShopContext } from './shop-context';

type ShopGateState =
  | { status: 'loading' }
  | { status: 'ineligible' }
  | { status: 'error' }
  | { status: 'ready'; shop: Shop };

/** Runs the GET-or-create-shop bootstrap once per `attempt`. */
async function resolveShop(): Promise<Shop> {
  const existing = await getShop();
  if (existing) {
    return existing;
  }
  await createShop();
  const created = await getShop();
  if (!created) {
    throw new Error('shop missing after creation');
  }
  return created;
}

/**
 * Gates the app behind plan eligibility and a resolved shop.
 *
 * @param props.children - The authenticated, shop-scoped app subtree.
 *
 * @returns A loading/eligibility/error screen, or the app wrapped in the shop context.
 */
export function ShopGate({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const [state, setState] = useState<ShopGateState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });

    const bootstrap = async (): Promise<void> => {
      const project = await getProject();
      const eligible =
        ALLOW_ALL_PLANS ||
        isShopEligible(
          project.projectPlan,
          project.trialExpirationDate,
          new Date(),
        );
      if (!eligible) {
        if (active) setState({ status: 'ineligible' });
        return;
      }
      const shop = await resolveShop();
      if (active) setState({ status: 'ready', shop });
    };

    bootstrap().catch(() => {
      if (active) setState({ status: 'error' });
    });

    return () => {
      active = false;
    };
  }, [attempt]);

  if (state.status === 'ineligible') {
    return <PlanNotEligibleScreen />;
  }
  if (state.status === 'error') {
    return <ShopErrorScreen onRetry={() => setAttempt((n) => n + 1)} />;
  }
  if (state.status === 'loading') {
    return <NoticeScreen title={intl.formatMessage(messages.shopPreparing)} />;
  }
  return (
    <ShopContext.Provider value={state.shop}>{children}</ShopContext.Provider>
  );
}
